import type { Element } from './constants'

/**
 * 간지 기본표와 관계 규칙. 오늘의 운세와 궁합이 같이 쓰는 순수 함수들이다.
 * 만세력 라이브러리(lunar-javascript)에 기대지 않는다 — 첫 화면에서 가볍게 돌아야 한다.
 */

export const STEMS = '甲乙丙丁戊己庚辛壬癸'
export const BRANCHES = '子丑寅卯辰巳午未申酉戌亥'

export const STEM_ELEMENT: Element[] = ['wood', 'wood', 'fire', 'fire', 'earth', 'earth', 'metal', 'metal', 'water', 'water']
const BRANCH_ELEMENT: Element[] = [
  'water', 'earth', 'wood', 'wood', 'earth', 'fire', 'fire', 'earth', 'metal', 'metal', 'earth', 'water',
]

/** 60갑자의 n 번째 (0 = 甲子 … 59 = 癸亥) */
export function pillarAt(n: number): string {
  return STEMS[n % 10]! + BRANCHES[n % 12]!
}

export function stemElement(stem: string): Element {
  const i = STEMS.indexOf(stem)
  if (i < 0) throw new Error(`천간이 아니다: ${stem}`)
  return STEM_ELEMENT[i]!
}

export function branchElement(branch: string): Element {
  const i = BRANCHES.indexOf(branch)
  if (i < 0) throw new Error(`지지가 아니다: ${branch}`)
  return BRANCH_ELEMENT[i]!
}

/** 상생 순서: 목 → 화 → 토 → 금 → 수 → 목 */
const GENERATES: Record<Element, Element> = {
  wood: 'fire',
  fire: 'earth',
  earth: 'metal',
  metal: 'water',
  water: 'wood',
}

/** 상극: 목극토, 토극수, 수극화, 화극금, 금극목 */
const CONTROLS: Record<Element, Element> = {
  wood: 'earth',
  earth: 'water',
  water: 'fire',
  fire: 'metal',
  metal: 'wood',
}

export function generates(from: Element, to: Element): boolean {
  return GENERATES[from] === to
}

export function controls(from: Element, to: Element): boolean {
  return CONTROLS[from] === to
}

/**
 * 나(일간 오행)를 기준으로 본 상대 오행의 자리.
 *  same     비겁 — 같은 기운
 *  output   식상 — 내가 낳는 기운
 *  wealth   재성 — 내가 다스리는 기운
 *  officer  관성 — 나를 다스리는 기운
 *  resource 인성 — 나를 낳는 기운
 * 화면과 AI 에는 이 이름을 그대로 내보내지 않는다 (사주 용어 금지).
 */
export type Relation = 'same' | 'output' | 'wealth' | 'officer' | 'resource'

export function relationOf(me: Element, other: Element): Relation {
  if (me === other) return 'same'
  if (generates(me, other)) return 'output'
  if (controls(me, other)) return 'wealth'
  if (controls(other, me)) return 'officer'
  return 'resource'
}

/** 천간합: 甲己 乙庚 丙辛 丁壬 戊癸 */
export function stemsCombine(a: string, b: string): boolean {
  const i = STEMS.indexOf(a)
  const j = STEMS.indexOf(b)
  return i >= 0 && j >= 0 && Math.abs(i - j) === 5
}

/** 천간충: 甲庚 乙辛 丙壬 丁癸 (戊己 는 충이 없다) */
export function stemsClash(a: string, b: string): boolean {
  const pairs = ['甲庚', '乙辛', '丙壬', '丁癸']
  return pairs.includes(a + b) || pairs.includes(b + a)
}

/** 지지 육합: 子丑 寅亥 卯戌 辰酉 巳申 午未 */
export function branchesCombine(a: string, b: string): boolean {
  const pairs = ['子丑', '寅亥', '卯戌', '辰酉', '巳申', '午未']
  return pairs.includes(a + b) || pairs.includes(b + a)
}

/** 지지 충: 서로 여섯 칸 떨어진 짝 */
export function branchesClash(a: string, b: string): boolean {
  const i = BRANCHES.indexOf(a)
  const j = BRANCHES.indexOf(b)
  return i >= 0 && j >= 0 && Math.abs(i - j) === 6
}

/** 지지 삼합의 같은 무리: 申子辰 寅午戌 巳酉丑 亥卯未 (서로 다른 두 글자일 때) */
export function branchesTrine(a: string, b: string): boolean {
  if (a === b) return false
  const groups = ['申子辰', '寅午戌', '巳酉丑', '亥卯未']
  return groups.some((g) => g.includes(a) && g.includes(b))
}

/**
 * 날짜의 일진(60갑자). 2000-01-01 = 戊午(인덱스 54)에서 하루씩 센다.
 * 만세력 검증 때 60일 주기 앵커로 쓴 값과 같다.
 */
export function dayGanZhi(isoDate: string): string {
  const [y, m, d] = isoDate.split('-').map(Number)
  if (!y || !m || !d) throw new Error(`날짜 형식이 아니다: ${isoDate}`)
  const days = Math.round((Date.UTC(y, m - 1, d) - Date.UTC(2000, 0, 1)) / 86_400_000)
  return pillarAt((((54 + days) % 60) + 60) % 60)
}

/** 사용자 기기의 현지 날짜 YYYY-MM-DD */
export function localDateISO(now: Date = new Date()): string {
  const p = (x: number) => String(x).padStart(2, '0')
  return `${now.getFullYear()}-${p(now.getMonth() + 1)}-${p(now.getDate())}`
}
